import { Suspense, lazy } from 'react';
import styles from './RefillCheckoutModal.module.css';

/**
 * The NEW-card half of the refill modal's payment section, routed by the payment switch.
 * Card #54400 (D4).
 *
 * Incumbent arm (`hostedKey` null): the raw card fields the modal has always rendered, moved here
 * unchanged. Hosted arm: the provider's own Element mints a payment method in its iframe, so the
 * card number never reaches this origin — the refill server refuses a raw card on that arm
 * (`RAW_CARD_NOT_ACCEPTED`), which is why the modal could not take a refill there at all.
 *
 * 🔑 The Element is LAZY for the same reason CheckoutPage's is: a static import would put the
 * provider SDK in the main chunk of every build, including one made with the switch off.
 */
const StripePaymentElement = lazy(() => import('../forms/StripePaymentElement'));

const formatCardNumber = (value) => {
  const cleaned = value.replace(/\D/g, '').slice(0, 16);
  return cleaned.match(/.{1,4}/g)?.join(' ') ?? '';
};

const formatExpiry = (value) => {
  const cleaned = value.replace(/\D/g, '').slice(0, 4);
  return cleaned.length >= 3 ? `${cleaned.slice(0, 2)}/${cleaned.slice(2)}` : cleaned;
};

const RefillCardEntry = ({ hostedKey, card, onCardChange, mintedMethodId, onMinted }) => (
  hostedKey ? (
    <Suspense fallback={<p className={styles.stateText}>Preparing secure card entry...</p>}>
      <StripePaymentElement
        publishableKey={hostedKey}
        onPaymentMethod={onMinted}
        submitLabel={mintedMethodId ? 'Card ready' : 'Use this card'}
      />
      {mintedMethodId && (
        <p className={styles.successText} role="status">
          Card confirmed with the payment provider. Pay below.
        </p>
      )}
    </Suspense>
  ) : (
    <div className={styles.cardFields}>
      <input
        type="text"
        inputMode="numeric"
        autoComplete="cc-number"
        className={styles.input}
        placeholder="Card number"
        value={card.cardNumber}
        onChange={(e) => onCardChange({ ...card, cardNumber: formatCardNumber(e.target.value) })}
      />
      <div className={styles.cardRow}>
        <input
          type="text"
          inputMode="numeric"
          autoComplete="cc-exp"
          className={styles.input}
          placeholder="MM/YY"
          value={card.expirationDate}
          onChange={(e) => onCardChange({ ...card, expirationDate: formatExpiry(e.target.value) })}
        />
        <input
          type="text"
          inputMode="numeric"
          autoComplete="cc-csc"
          className={styles.input}
          placeholder="CVV"
          value={card.cvv}
          onChange={(e) => onCardChange({ ...card, cvv: e.target.value.replace(/\D/g, '').slice(0, 4) })}
        />
      </div>
    </div>
  )
);

export default RefillCardEntry;
